/**
 * @m8t-stack/brain-engine — the seam-3 contract.
 * VERBATIM from E1-F2 DESIGN §§3,4,5 (and EPIC §5.3). Additive-only; F3 + E2
 * consume this exact shape. LedgerRow + RoundEnrichment are owned by the
 * agent-ledger read split (DESIGN §1) — imported here, never redefined.
 */
import type { LedgerRow, RoundEnrichment } from "@m8t-stack/agent-ledger/read";
/** Re-export the owned read types under the contract barrel — consumers reference them by CONTRACT name (DESIGN §1). */
export type { LedgerRow, RoundEnrichment } from "@m8t-stack/agent-ledger/read";

/** Pinned per F1 RETRO §4 #2 — stamped into the cursor; bumped only on a contract break. */
export const ENGINE_VERSION = "0.1.0";

/** Watermark trails real time by this margin (>longest plausible turn+emit latency); DESIGN §3. */
export const DEFAULT_SAFETY_LAG_SECONDS = 3600;

/** The closed source set; anything outside routes to `unknown-source` (DESIGN §4, §7). */
export const SOURCES = ["webapp", "a2a", "mcp", "telegram"] as const;

/** Physical PK → canonical worker. `azure-advisor` is a frozen pre-rename alias of `azzy` (DESIGN §0, §7). */
export const WORKER_ALIASES: Record<string, string> = { "azure-advisor": "azzy" };

/**
 * Resolve a physical worker/PartitionKey to its canonical lowercase form: lowercase
 * first, then apply the frozen alias map (e.g. `azure-advisor` → `azzy`, DESIGN §7).
 * The single resolver — cursor.ts and ledger-source.ts both import this. Pure.
 */
export function canonicalWorker(pk: string): string {
  const lower = pk.toLowerCase();
  return WORKER_ALIASES[lower] ?? lower;
}

/** The ordered, exhaustive, mutually-exclusive skip partition (DESIGN §4). */
export type SkipReason =
  | "not-target-worker"
  | "no-attribution"
  | "a2a-unlinked"
  | "no-conv-id"
  | "recovery-no-content"
  | "conv-fetch-failed"
  | "auth-failed"
  | "outside-trace-window"
  | "unknown-source";

export interface SkipEntry {
  reason: SkipReason;
  count: number;
  sampleIds: string[];
  lastError?: string;
}

/** Structured, NOT flattened — PII + misrouting need the source+ref split (DESIGN §5). */
export interface NormalizedUser {
  source: string;
  ref: string;
  display?: string;
}

export interface ToolUse {
  name: string;
  type: string;
  count: number;
  queries: string[];
  sources: string[];
}

/** A single transcript turn; `at` is optional until the §14 timestamp doctrine lands. */
export interface NormalizedItem {
  role: string;
  text: string;
  at?: string;
}

export interface NormalizedConversation {
  conversationId: string;
  source: string;
  attribution: {
    worker: string;
    user?: NormalizedUser;
    caller?: string;
    target?: string;
    delegationId?: string;
  };
  startedAt: string;
  lastAt: string;
  rounds: number;
  outcome: string;
  /** Transcript, oldest-first (conversations.items.list is newest-first → reversed). */
  items: NormalizedItem[];
  enrichment?: { inputTokens: number; outputTokens: number; tools: ToolUse[] };
  recoveredVia?: "trace";
  /**
   * Source-row provenance (DESIGN §4.1) — the physical PK + the storage RowKeys
   * (and their emit-times) of the LedgerRows this conversation was harvested from.
   * Stamped by runPipeline where normalize() is called; consumed by capCorpus to
   * recompute a PARTIAL cursor advance over only the KEPT conversations' rows. A
   * conversation groups ≥1 row, so `rows` is a SET (a2a/multi-round span many).
   * Optional only for the contract's additive guarantee — the harvest always sets it.
   */
  provenance?: { pk: string; rows: ConversationRow[] };
}

/** One source LedgerRow behind a conversation: its dedup RowKey + emit-time. The
 *  exact fields computeWatermark reads, so capCorpus re-derives the kept-set
 *  watermark with the SAME math as the full-corpus advance (DESIGN §3/§4.1). */
export interface ConversationRow {
  rowKey: string;
  eventTimestamp: string;
}

/** RETRO harvest metrics (DESIGN §5); `recovered` counts only content-proven recoveries. */
export interface DreamStats {
  ledgerRows: number;
  groups: number;
  consumed: number;
  recovered: number;
  skipped: number;
  latencyMs: number;
}

export interface NormalizedDreamInput {
  worker: string;
  window: { from?: string; to: string };
  conversations: NormalizedConversation[];
  skipLedger: SkipEntry[];
  stats: DreamStats;
  /** The write-gated cursor advance (DESIGN §2.2). Computed every run; committed by runDream only after a successful brain write. */
  advance: AdvancePlan;
}

/** Per-physical-PK watermark (DESIGN §3). `ts` = min(max(consumed emit-time), now − safetyLag). */
export interface PkWatermark {
  ts: string;
  consumedRowKeys: string[];
}

/** One cursor row per canonical worker; `watermarks` keyed by physical PK (DESIGN §3). */
export interface DreamCursor {
  worker: string;
  watermarks: Record<string, PkWatermark>;
  safetyLagSeconds: number;
  engineVersion: string;
  lastDreamAt?: string;
}

/**
 * Write-gated cursor advance (DESIGN §1/§2.2; folds CRITICAL F1). runPipeline
 * COMPUTES this; runDream COMMITS it via commitCursorAdvance ONLY after the brain
 * write succeeds. Dry-run still computes it (for the would-advance preview) but
 * never commits. Carries the per-PK watermarks + the rows consumed this run +
 * the fetch-error fence so the commit reproduces the exact same advance.
 */
export interface AdvancePlan {
  worker: string;
  watermarks: Record<string, PkWatermark>;     // per physical PK
  consumedRowsByPk: Record<string, string[]>;   // rowKeys consumed this run (overlap-dedup already correct)
  failedTsByPk: Record<string, string>;         // PK -> earliest fetch-errored ts (the fence; never advance past it)
  /**
   * The inputs buildAdvancePlan used, carried so capCorpus can RE-derive a partial
   * advance over only the kept conversations' rows via the SAME computeWatermark +
   * monotonic re-pin + fence (DESIGN §3/§4.1) — never by reusing the full-corpus ts
   * with a filtered key list (which would leave ts/consumedRowKeys inconsistent).
   * Optional only for the contract's additive guarantee; runPipeline always sets them.
   */
  now?: string;                                 // the injected clock (ISO) the watermark was clamped against
  safetyLagSeconds?: number;                    // the lag used for the (now − lag) clamp + overlap floor
  priorTsByPk?: Record<string, string>;         // the committed prior watermark ts per PK (the monotonic re-pin floor)
}

/** Injected source interfaces — the seam (DESIGN §2). Concrete impls built at the entry point; tests inject fakes. */
export interface LedgerSource {
  /** PK-scoped (worker + frozen alias), watermark+overlap; auto-paginated, no `$top` (DESIGN §3). */
  rowsSince(physicalPks: string[], cursor: DreamCursor): Promise<LedgerRow[]>;
}

export interface ConversationSource {
  /** Throws ConvFetchError on 404/terminal; returns transcript items (DESIGN §9). */
  items(conversationId: string): Promise<NormalizedItem[]>;
  /** The a2a recovery gate's app-tag read (DESIGN §2.3, Option A); null = un-provable → caller hard-gates it out. */
  retrieveApp?(conversationId: string): Promise<string | null>;
}

export interface TraceSource {
  enrich(
    respIds: string[],
    window: { from: string; to: string },
  ): Promise<Map<string, RoundEnrichment>>;
  /** Returns a validated `conv_` id, or null if outside-window / not recoverable (DESIGN §6). */
  recoverConversationId(
    respId: string,
    window: { from: string; to: string },
  ): Promise<string | null>;
}

export interface Cursor {
  /** Tolerates a missing table → absent-cursor (from-the-beginning); never creates the table (DESIGN §3). */
  read(worker: string): Promise<DreamCursor>;
  /** NON-dry-run only (F3 path); never advances past a fetch-errored row (DESIGN §2, §3). */
  advance(worker: string, cursor: DreamCursor): Promise<void>;
}

/** Terminal transcript-fetch failure; carries the disposition kind (DESIGN §9). */
export class ConvFetchError extends Error {
  constructor(
    public kind: "not-found" | "terminal" | "auth",
    message: string,
  ) {
    super(message);
    this.name = "ConvFetchError";
  }
}
